// OttoSchedule.tsx - 예약: 정해진 시각에 조명, 라디오, 내 음악을 켜고 끈다.
// 보드 schedule.ino 와 같은 형식의 문자열로 예약 전체를 주고받는다.
//   예약 하나 = "대상,켜는 분,끄는 분,요일,사용,값"   예) "1,1080,1200,62,1,3"
//   대상 0 조명 / 1 라디오 / 2 내 음악, 분은 자정부터 지난 분(-1 = 없음), 요일은 비트(1=일 … 64=토, 127 = 매일)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MQTTService from './MQTTService';

export const SCHED_MAX = 8; // 보드의 SCHED_MAX 와 같아야 한다 (MQTT 메시지가 300바이트 이하여야 해서 8개)
const MUSIC_SLOTS = 6;      // 보드의 MUSIC_MAX 와 같다
const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];
const ALL_DAYS = 127;
const TARGET_NAMES = ['조명', '라디오', '내 음악'];
const SCHED_ERROR_TEXT: Record<string, string> = {
  bad_param: '예약 형식이 맞지 않아 보드가 받지 않았습니다.',
  bad_action: '보드 펌웨어가 예약을 지원하지 않습니다. 새 펌웨어를 올리세요.',
};

interface Slot {
  type: number;
  on: string;   // 'HH:MM' 또는 '' (없음)
  off: string;  // 'HH:MM' 또는 '' (없음)
  days: number;
  en: boolean;
  param: number;
}

const minToText = (m: number): string =>
  m < 0 ? '' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

const textToMin = (t: string): number => {
  const mt = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!mt) return -1;
  const h = Number(mt[1]), mi = Number(mt[2]);
  return h > 23 || mi > 59 ? -1 : h * 60 + mi;
};

const parseList = (s: string): Slot[] => {
  if (!s) return [];
  return s.split(';').map((part) => {
    const v = part.split(',').map(Number);
    return { type: v[0] || 0, on: minToText(v[1]), off: minToText(v[2]), days: v[3] ?? ALL_DAYS, en: v[4] === 1, param: v[5] || 0 };
  });
};

const toList = (slots: Slot[]): string =>
  slots.map((s) => [s.type, textToMin(s.on), textToMin(s.off), s.days, s.en ? 1 : 0, s.param].join(',')).join(';');

interface Props {
  mac: string;
  email: string;
  mqttMessage?: string;
  stations: string[]; // 라디오 방송국 이름 (보드의 순서와 같다)
}

const cardInput: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '10px', background: '#f8fafc', fontSize: '0.9rem',
};

const OttoSchedule: React.FC<Props> = ({ mac, email, mqttMessage, stations }) => {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [dirty, setDirty] = useState(false);     // 고쳤지만 아직 보드에 저장하지 않았는지
  const [boardTime, setBoardTime] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const dirtyRef = useRef(false);

  const publish = useCallback((action: string, extra: Record<string, any> = {}) => {
    if (!mac || !email) return;
    MQTTService.getInstance().publish(
      `i2r/${email}/in`,
      JSON.stringify({ command: 'ottoSched', mac, email, action, ...extra })
    );
  }, [mac, email]);

  // 처음 열 때 보드의 예약을 읽어 온다
  useEffect(() => { publish('get'); }, [publish]);

  useEffect(() => {
    if (!mqttMessage) return;
    try {
      const m = JSON.parse(mqttMessage);
      if (m.mac !== mac || m.command !== 'ottoSched') return;
      if (m.ok === 0 || m.ok === false) {
        setError(SCHED_ERROR_TEXT[m.err] || `예약 오류 (${m.err || '알 수 없음'})`);
        return;
      }
      if (typeof m.time === 'string') setBoardTime(m.time);
      if (m.action === 'set') {
        dirtyRef.current = false;
        setDirty(false);
        setError('');
        setStatus('보드에 저장했습니다.');
        setSlots(parseList(m.text || ''));
      } else if (m.action === 'get' && !dirtyRef.current) { // 고치는 중이면 덮어쓰지 않는다
        setSlots(parseList(m.text || ''));
      }
    } catch (e) { /* 무시 */ }
  }, [mqttMessage, mac]);

  const change = (i: number, patch: Partial<Slot>) => {
    setSlots((prev) => prev.map((s, k) => (k === i ? { ...s, ...patch } : s)));
    dirtyRef.current = true;
    setDirty(true);
    setStatus('');
  };

  const add = () => {
    if (slots.length >= SCHED_MAX) return;
    setSlots((prev) => [...prev, { type: 0, on: '07:00', off: '08:00', days: ALL_DAYS, en: true, param: 0 }]);
    dirtyRef.current = true;
    setDirty(true);
    setStatus('');
  };

  const remove = (i: number) => {
    setSlots((prev) => prev.filter((_, k) => k !== i));
    dirtyRef.current = true;
    setDirty(true);
    setStatus('');
  };

  const save = () => {
    for (let i = 0; i < slots.length; i++) {
      if (textToMin(slots[i].on) < 0 && textToMin(slots[i].off) < 0) {
        setError(`${i + 1}번 예약: 켜는 시각이나 끄는 시각을 입력하세요.`);
        return;
      }
    }
    setError('');
    setStatus('저장하는 중...');
    publish('set', { text: toList(slots) });
  };

  const labelStyle: React.CSSProperties = { fontWeight: 600, color: '#334155', fontSize: '0.85rem' };

  return (
    <div className="otto-subpanel">
      <div className="otto-subpanel-title">
        예약 {boardTime && <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.85rem' }}>(로봇 시각 {boardTime})</span>}
      </div>
      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '12px', lineHeight: 1.5 }}>
        정해진 시각에 조명, 라디오, 내 음악을 켜고 끕니다. 같은 것을 여러 번 겹쳐서 넣어도 됩니다. (최대 {SCHED_MAX}개)
        시각은 로봇이 인터넷에서 받은 한국 시간입니다. 조명은 [조명]에서 마지막으로 고른 색과 밝기로 켜지고,
        소리는 라디오와 내 음악에서 정한 볼륨으로 나옵니다.
      </div>

      {slots.length === 0 && (
        <div style={{ padding: '14px', textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: '12px', marginBottom: '12px' }}>
          예약이 없습니다. [예약 추가]를 누르세요.
        </div>
      )}

      {slots.map((s, i) => (
        <div key={i} style={{
          border: '1px solid #e2e8f0', borderRadius: '14px', padding: '12px', marginBottom: '10px',
          background: s.en ? '#ffffff' : '#f1f5f9', opacity: s.en ? 1 : 0.75,
        }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontWeight: 700, color: '#1e293b' }}>{i + 1}</span>
            <select value={s.type} onChange={(e) => change(i, { type: Number(e.target.value), param: 0 })} style={cardInput}>
              {TARGET_NAMES.map((n, t) => <option key={n} value={t}>{n}</option>)}
            </select>
            {s.type === 1 && (
              <select value={s.param} onChange={(e) => change(i, { param: Number(e.target.value) })} style={{ ...cardInput, maxWidth: '260px' }}>
                {stations.map((n, k) => <option key={n} value={k}>{n}</option>)}
              </select>
            )}
            {s.type === 2 && (
              <select value={s.param} onChange={(e) => change(i, { param: Number(e.target.value) })} style={cardInput}>
                <option value={0}>처음 곡부터</option>
                {Array.from({ length: MUSIC_SLOTS }, (_, k) => <option key={k + 1} value={k + 1}>{k + 1}번 곡</option>)}
              </select>
            )}
            <label style={{ ...labelStyle, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input type="checkbox" checked={s.en} onChange={(e) => change(i, { en: e.target.checked })} /> 사용
            </label>
            <button type="button" className="action-btn btn-secondary" onClick={() => remove(i)}>삭제</button>
          </div>

          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
            <label style={labelStyle}>
              켜는 시각{' '}
              <input type="time" value={s.on} onChange={(e) => change(i, { on: e.target.value })} style={cardInput} />
            </label>
            <label style={labelStyle}>
              끄는 시각{' '}
              <input type="time" value={s.off} onChange={(e) => change(i, { off: e.target.value })} style={cardInput} />
            </label>
            <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
              시각을 비우면 그 동작은 하지 않습니다. 끄는 시각이 더 이르면 다음 날 끕니다.
            </span>
          </div>

          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            {DAY_NAMES.map((d, k) => {
              const on = (s.days & (1 << k)) !== 0;
              return (
                <button
                  key={d}
                  type="button"
                  className={`action-btn ${on ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ minWidth: '38px', padding: '6px 8px' }}
                  onClick={() => change(i, { days: s.days ^ (1 << k) })}
                >
                  {d}
                </button>
              );
            })}
            <button type="button" className="action-btn btn-secondary" style={{ padding: '6px 10px' }}
              onClick={() => change(i, { days: s.days === ALL_DAYS ? 0 : ALL_DAYS })}>
              {s.days === ALL_DAYS ? '모두 해제' : '매일'}
            </button>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="action-btn btn-secondary" onClick={add} disabled={slots.length >= SCHED_MAX}>
          예약 추가
        </button>
        <button type="button" className={`action-btn ${dirty ? 'btn-primary' : 'btn-secondary'}`} onClick={save}>
          저장{dirty ? ' (저장하지 않은 변경이 있습니다)' : ''}
        </button>
        <button type="button" className="action-btn btn-secondary" onClick={() => publish('get')} disabled={dirty}>
          다시 읽기
        </button>
        {status && <span style={{ fontSize: '0.88rem', color: '#2563eb', fontWeight: 500 }}>{status}</span>}
      </div>

      {error && (
        <div style={{ marginTop: '10px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', color: '#b91c1c', fontSize: '0.88rem' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default OttoSchedule;
